/**
 * Firebase Storage layout — REQUIREMENTS.md §5.4, §9.
 *
 * Every path starts with `users/{userId}/` so the security rules can authorize
 * from the path alone, without reading a Firestore document. A rules check
 * that needs a `get()` costs a document read on every file access and fails
 * open if the document is missing; a path check cannot.
 *
 * §9 requires that a capture is never lost: the raw file is written here
 * before any processing runs, so a failed extraction leaves the original
 * intact and re-runnable.
 */

export const STORAGE_ROOT = 'users'

/** Raw upload, exactly as captured. Never modified or deleted by processing. */
export function originalReceiptPath(
  userId: string,
  receiptId: string,
  extension: string,
): string {
  return `${STORAGE_ROOT}/${userId}/receipts/${receiptId}/original.${normalizeExtension(extension)}`
}

/** Normalized single- or multi-page PDF produced at ingest (§5.4). */
export function renderedReceiptPdfPath(userId: string, receiptId: string): string {
  return `${STORAGE_ROOT}/${userId}/receipts/${receiptId}/rendered.pdf`
}

/** One image per page, for assembling the reconciliation packet (§8.1). */
export function receiptPageImagePath(
  userId: string,
  receiptId: string,
  pageNumber: number,
): string {
  return `${STORAGE_ROOT}/${userId}/receipts/${receiptId}/pages/${String(pageNumber).padStart(3, '0')}.png`
}

/**
 * The forwarded message exactly as received — §6.1, §9.
 *
 * Kept whole and untouched even though the useful part is extracted straight
 * away. Everything downstream is a lossy interpretation: an attachment picked
 * from several, an HTML body rendered by a browser, a total read by a model.
 * When one of those turns out wrong, the raw message is the only thing that
 * can settle what actually arrived.
 */
export function rawEmailPath(userId: string, receiptId: string): string {
  return `${STORAGE_ROOT}/${userId}/receipts/${receiptId}/email.eml`
}

/** Photographed credit-card statement pages (§5.5a). Phase 5. */
export function statementImagePath(
  userId: string,
  statementId: string,
  pageNumber: number,
): string {
  return `${STORAGE_ROOT}/${userId}/statements/${statementId}/pages/${String(pageNumber).padStart(3, '0')}.jpg`
}

/** Generated reconciliation packet (§8.1). Phase 5. */
export function packetPath(
  userId: string,
  periodId: string,
  format: 'pdf' | 'xlsx',
): string {
  return `${STORAGE_ROOT}/${userId}/packets/${periodId}/packet.${format}`
}

/** Extract the owning userId from any storage path, or null if malformed. */
export function userIdFromStoragePath(path: string): string | null {
  const match = /^users\/([^/]+)\//.exec(path)
  return match?.[1] ?? null
}

/** Extract the receiptId from a receipt storage path, or null. */
export function receiptIdFromStoragePath(path: string): string | null {
  const match = /^users\/[^/]+\/receipts\/([^/]+)\//.exec(path)
  return match?.[1] ?? null
}

// ── Upload constraints ───────────────────────────────────────────────────────

/**
 * Content types accepted on upload.
 *
 * HEIC is included because it is the iPhone camera's default format. Claude's
 * vision API does not accept it, so anything stored as HEIC must be converted
 * before extraction — see the note in the extraction function. Rejecting it at
 * upload instead would mean silently refusing photos straight from Matt's
 * camera roll.
 */
export const ACCEPTED_UPLOAD_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
  'application/pdf',
] as const

export type AcceptedUploadType = (typeof ACCEPTED_UPLOAD_TYPES)[number]

/** Formats Claude's vision API accepts directly, without conversion. */
export const CLAUDE_VISION_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
] as const

export function needsConversionForVision(contentType: string): boolean {
  return !(CLAUDE_VISION_TYPES as readonly string[]).includes(contentType)
}

export function isAcceptedUploadType(contentType: string): boolean {
  return (ACCEPTED_UPLOAD_TYPES as readonly string[]).includes(contentType)
}

/**
 * 25 MB. A modern phone photo is 3–8 MB and a scanned multi-page PDF can reach
 * 15 MB; beyond 25 MB it's a mis-drop, not a receipt. Enforced in the storage
 * rules, not just the client, since the client is not the security boundary.
 */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024

/**
 * The lowercased extension of a filename, or `''` if it has none.
 *
 * For logging a received attachment without its basename. A filename a sender
 * chose can carry a person's name or the purpose of a document; the extension
 * is the only part worth a log line — it can disagree with the declared content
 * type, which is a real signal — and the rest should not be written down.
 *
 * A leading-dot name (`.htaccess`) and a trailing dot (`scan.`) both count as
 * having no extension.
 */
export function filenameExtension(filename: string): string {
  const dot = filename.lastIndexOf('.')
  if (dot <= 0 || dot === filename.length - 1) return ''
  return filename.slice(dot + 1).toLowerCase()
}

/** Map a content type to the extension used in the stored filename. */
export function extensionForContentType(contentType: string): string {
  const map: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'image/heic': 'heic',
    'image/heif': 'heif',
    'application/pdf': 'pdf',
  }
  return map[contentType] ?? 'bin'
}

/**
 * The extension as it may appear in a storage path.
 *
 * Reduced to letters and digits, which is stricter than it looks necessary and
 * is the point: this is the only caller-supplied string that reaches a storage
 * path, and every path's `users/{userId}/` prefix is what the security rules
 * authorize from. `filenameExtension` — exported from this same module, and
 * already imported alongside `originalReceiptPath` in the inbound-email
 * function — returns whatever follows the last dot of a *sender-chosen*
 * filename. Wiring those two together is a one-line change someone will
 * reasonably make, and `receipt.pdf/../../<other-uid>/receipts/x/original`
 * would then write outside the forwarding user's folder, from a function that
 * runs as Admin and meets no rules at all. Constrained here so no caller can
 * reintroduce that.
 */
function normalizeExtension(extension: string): string {
  return extension.replace(/^\./, '').toLowerCase().replace(/[^a-z0-9]/g, '') || 'bin'
}

/**
 * What a downloaded packet should be called — `Callison-June-2026-Pcard-
 * Reconciliation.xlsx`.
 *
 * Separate from `packetPath` on purpose. The stored path is an identifier: it
 * is written into `reconciliationPeriods` and must not move when a cardholder
 * is renamed or a cycle relabelled. This is only what the browser saves the
 * file as, and it is carried by the object's `Content-Disposition` rather than
 * by a `download` attribute on the link — which browsers ignore for a
 * cross-origin URL, and every Firebase Storage URL is cross-origin.
 *
 * Deliberately ASCII: a non-ASCII filename in a `Content-Disposition` header
 * needs RFC 5987 encoding, and the fallback when a client gets that wrong is
 * mojibake in the download folder. Accents are folded rather than dropped, so
 * "Muñoz" saves as "Munoz" and not "Muoz".
 *
 * Any part that is empty is left out rather than leaving a stray separator, so
 * an account with no cardholder name still gets a sensible name instead of
 * `-June-2026-…`.
 */
export function packetFileName(
  cardholderName: string,
  monthYear: string,
  format: 'pdf' | 'xlsx',
): string {
  const parts = [surnameOf(cardholderName), monthYear, 'Pcard Reconciliation']
    .map(asciiSlug)
    .filter((part) => part !== '')
  return `${parts.join('-')}.${format}`
}

/** The last word of a name — what a filing cabinet would sort it under. */
function surnameOf(cardholderName: string): string {
  const words = cardholderName.trim().split(/\s+/)
  return words[words.length - 1] ?? ''
}

function asciiSlug(value: string): string {
  return value
    .normalize('NFD')
    // Combining marks left behind by the decomposition above. Matched by
    // property rather than by code-point range: the literal characters are
    // invisible in an editor and a mistyped range fails silently.
    .replace(/\p{Diacritic}/gu, '')
    // Apostrophes close up rather than becoming separators: O'Brien, not
    // O-Brien.
    .replace(/['’]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * A `Content-Disposition` value naming the file.
 *
 * `inline` for the PDF so the packet still opens in a tab to be read and
 * printed — the browser uses this name when it is saved from there. `xlsx` has
 * no viewer, so it downloads either way and says so.
 */
export function packetContentDisposition(fileName: string): string {
  const disposition = fileName.endsWith('.pdf') ? 'inline' : 'attachment'
  // `asciiSlug` has already removed quotes and non-ASCII, so plain quoting is
  // safe and no RFC 5987 `filename*` form is needed.
  return `${disposition}; filename="${fileName}"`
}
